import { Flex, Form, notification } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
    getApiError,
    patchSchedulerSetSchedulePointsThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";

import BaseButton from "@/components/BaseButton";
import { BaseModal } from "@/components/BaseModal";
import { useAppDispatch } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { ScheduleRepeatForm } from "@/pages/SV/RouteManagement/components/ScheduleRepeatForm";
import { ScheduleRepeatFormFields } from "@/pages/SV/RouteManagement/components/ScheduleRepeatForm/ScheduleRepeatForm.types";
import {
    RouteEditorModal,
    RouteEditorModalPayloads,
} from "@/pages/SV/RouteManagement/RouteEditor/RouteEditor.types";
import { updateSchedule } from "@/store/slices/sv";

interface EditScheduleModalProps {
    onClose: () => void;
    payload: RouteEditorModalPayloads[RouteEditorModal.EditSchedule];
    onApprove: () => Promise<void>;
}

export const EditScheduleModal = ({
    payload,
    onClose,
    onApprove,
}: EditScheduleModalProps) => {
    const dispatch = useAppDispatch();

    const { schedule } = payload;

    const [hasFormErrors, setHasFormErrors] = useState(false);

    const [form] = Form.useForm<ScheduleRepeatFormFields>();

    useEffect(() => {
        form.setFieldsValue({
            weekly: schedule.weekly ?? 1,
            days: schedule.days ?? [],
            weekStartDate:
                schedule.startDate ??
                dayjs().startOf("week").format(backendDateFormat.date),
        });
    }, [schedule, form]);

    const [handleSave, saveLoading] = useAsyncAction(
        async (values: ScheduleRepeatFormFields) => {
            const { weekly, days, weekStartDate } = values;

            const updatedPoint = {
                ...schedule,
                weekly,
                days,
                startDate: weekStartDate,
            };

            if (updatedPoint.days.length === 0) {
                notification.error({
                    message: "Оберіть хоча б один день повторення.",
                });
                return;
            }

            if (payload.createMode) {
                dispatch(
                    updateSchedule({
                        marketId: updatedPoint.marketId,
                        updatedData: updatedPoint,
                    }),
                );
                onClose();
                return;
            }

            const { allSchedules, route } = payload as Extract<
                typeof payload,
                { createMode: false }
            >;

            const updatedSchedules = allSchedules.map((item) =>
                item.marketId === updatedPoint.marketId
                    ? { ...item, ...updatedPoint }
                    : item,
            );

            try {
                await dispatch(
                    patchSchedulerSetSchedulePointsThunk({
                        agentId: route.agentId,
                        ownerId: route.ownerId,
                        schedules: updatedSchedules,
                    }),
                ).unwrap();

                onClose();
                await onApprove();
            } catch (error) {
                const { displayMessage } = getApiError(error);
                notification.error({
                    message: "Не вдалося редагувати",
                    description: displayMessage,
                });
            }
        },
    );

    return (
        <BaseModal
            open
            onClose={onClose}
            actions={null}
            maskClosable={!saveLoading}
        >
            <Flex vertical gap={16}>
                <Form
                    form={form}
                    onFinish={handleSave}
                    disabled={saveLoading}
                    onFieldsChange={() => {
                        setHasFormErrors(
                            form.getFieldsError().some((f) => f.errors.length),
                        );
                    }}
                >
                    <ScheduleRepeatForm form={form} disabled={saveLoading} />
                </Form>

                <BaseButton
                    type="primary"
                    onClick={() => form.submit()}
                    loading={saveLoading}
                    disabled={hasFormErrors}
                >
                    Зберегти зміни
                </BaseButton>
            </Flex>
        </BaseModal>
    );
};
